import { CategoryTopic } from 'interface/CategoryTopic';
import React, { useEffect, useRef, useState } from 'react'
import styles from 'styles/components/CategoryReportDisplayItem.module.sass'
import TopicCard from './TopicCard';
import ReusableIcon from 'components/common/ReusableIcon';
import { useAuth } from 'hooks/AuthContext';
import { checkAccessToReport } from 'api/ReportApiService';
import { Icon } from '@iconify/react';
import { downloadReport } from 'api/ReportApiService';
import { useNavigate } from 'react-router-dom';
import Spinner from 'components/common/Spinner';
import { useMaintenance } from 'maintenance/index';

const tag = 'CategoryReportDisplayItem';
const accessCache = new Map<string, boolean>();

/**
 * Props for the CategoryReportDisplayItem component.
 */
interface CategoryReportDisplayItemProps {
    report: CategoryTopic;
}

/**
 * Get the description of a report.
 *
 * @param text - The description text to format.
 * @returns An array of JSX elements representing the formatted description.
 */
const getDescription = (text: string): JSX.Element[] => {
    return text
        ?.replace(/\\n/g, '\n')
        .split(/\n+/)
        .map((paragraph: string, index: number) => (
            <p key={index} className={styles['catchy-phrase']}>
                {paragraph.trim()}
            </p>
        ))
}

/**
 * CategoryReportDisplayItem component displays an individual report item within a category.
 *
 * @example
 * const report: CategoryTopic = {
 *   id: "12345",
 *   title: "Market Analysis 2023",
 *   dataset: "Global Markets",
 *   year: 2023,
 *   country: "USA",
 *   description: "An in-depth analysis of global markets.\nIncludes trends and forecasts.",
 *   meta: {
 *     price: 199,
 *     creditPrice: 2,
 *     month: "March"
 *   },
 *   links: {
 *     pdf: "https://example.com/report.pdf",
 *     excel: "https://example.com/report.xlsx"
 *   }
 * }
 * 
 * <CategoryReportDisplayItem report={report} />
 */
const CategoryReportDisplayItem: React.FC<CategoryReportDisplayItemProps> = ({ report }) => {
    const { user } = useAuth();
    const [hasAccess, setHasAccess] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const latestId = useRef<string | null>(null);
    const navigate = useNavigate();
    // Downloads are guarded under the 'report-purchase' area
    const { blocked: downloadsBlocked } = useMaintenance('report-purchase');

    useEffect(() => {
        if (!user || !report.id) return;

        if (accessCache.has(report.id)) {
            setHasAccess(Boolean(accessCache.get(report.id)));
            return;
        }

        let cancelled = false;
        latestId.current = report.id;

        (async () => {
            try {
                const access = await checkAccessToReport({ tag, id: report.id });
                if (!cancelled && latestId.current === report.id) {
                    accessCache.set(report.id, access.hasAccess);
                    setHasAccess(access.hasAccess);
                }
            } catch {
                if (!cancelled) {
                    accessCache.set(report.id, false);
                    setHasAccess(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [user, report.id]);

    /**
     * Handles the click event for the download report button.
     * @returns {Promise<void>}
     */
    const downloadReportButtonClicked = async () => {
        if (downloadsBlocked) return;
        const id = report.id;
        setDownloading(true);
        try {
            const url = await downloadReport({ tag, id })
            if (!url) {
                navigate(`/report/${id}`)
                return;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            navigate(`/report/${id}`)
        } finally {
            setDownloading(false);
        }
    }

    /**
     * Wrapper function for the download report button click event.
     * @returns {Promise<void>}
     */
    const downloadReportButtonWrapper = () => {
        downloadReportButtonClicked()
    }

    const navigateToReport = () => {
        navigate(`/report/${report.id}`);
    }

    return (
        <div key={report.id} className={styles['category-report-display-item']} onClick={navigateToReport}>
            <div className={styles['left-section']}>
                <TopicCard
                    id={report.id}
                    highlight={report.dataset || ''}
                    title={report.title}
                    year={report.year}
                    type={report.meta.type || ''}
                />
            </div>
            <div className={styles['right-section']}>
                <div className={styles['header']}>
                    <span className={styles['report-title']}>{report.title}</span>
                    {hasAccess ? (
                        <span className={styles['report-access']}>You own this report</span>
                    ) : report.meta.price && report.meta.creditPrice ? (
                        <div className={styles['report-price-container']}>
                            <span className={styles['report-price']}>${report.meta.price} USD</span>
                            <p>or</p>
                            <span className={styles['report-credit-price']}>{report.meta.creditPrice} {report.meta.creditPrice === 1 ? 'Credit' : 'Credits'}</span>
                        </div>
                    ) : report.meta.price === 0 ? (
                        <span className={styles['report-tag-free']}>Free</span>
                    ) : null}
                </div>
                <div className={styles['description']}>
                    {getDescription(report.description)}
                </div>
                <div className={styles['report-footer']}>
                    <div className={styles['report-tag']}>
                        <ReusableIcon icon={'cil:calendar'} className={styles['icon'] || 'icon'} color={'#707070'} />
                        {`${report.meta.month ? report.meta.month : ''} ${report.year}`}
                    </div>
                    <div className={styles['report-tag']}>
                        <ReusableIcon icon={'iconoir:page'} className={styles['icon'] || 'icon'} color={'#707070'} />
                        {report.country}
                    </div>
                    {hasAccess ? (
                        <button
                            className={styles['download-button']}
                            onClick={downloadReportButtonWrapper}
                            disabled={downloadsBlocked}
                            title={downloadsBlocked ? 'Downloads are temporarily unavailable. Please try again shortly.' : undefined}
                        >
                            {downloading ? <Spinner /> : <Icon icon={'cil:cloud-download'} className={styles['button-icon'] || 'button-icon'}  />}
                            <span>{downloading ? 'Preparing...' : downloadsBlocked ? 'Unavailable' : 'Download'}</span>
                        </button>
                    ) : (
                        <button className={styles['buy-now-button']} onClick={navigateToReport}>
                            <Icon icon={'mdi:cart-outline'} className={styles['button-icon'] || 'button-icon'} />
                            <span>Get Report</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CategoryReportDisplayItem;
